import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useForm } from "@mantine/form";
import { useAtom } from "jotai";

import { useAppliedDateRange } from "@prostocheck/data-web";
import { MAX_REGISTER_RANGE_DAYS, registerFiltersAtom } from "@prostocheck/shared";

import { IUseRegisterFilters } from "./use-register-filters.models";
import {
  createRegisterFiltersValidation,
  toDefaultRegisterFilters,
} from "./use-register-filters.utils";

/**
 * The register page's one filter form — genuinely local UI state, so it owns
 * the form itself rather than being handed one. Persisted in
 * `registerFiltersAtom` so it survives a remount (e.g. navigating between
 * registers in the sidebar and back). `range` is what `useReceipts` and
 * `useZReports` are both called with — one filter, two sections.
 */
export const useRegisterFilters = (): IUseRegisterFilters => {
  const { t } = useTranslation();
  const [persistedFilters, setPersistedFilters] = useAtom(registerFiltersAtom);

  const form = useForm({
    initialValues: persistedFilters ?? toDefaultRegisterFilters(),
    validate: createRegisterFiltersValidation(t),
  });

  useEffect(() => {
    setPersistedFilters(form.values);
  }, [form.values, setPersistedFilters]);

  const range = useAppliedDateRange(
    { from: form.values.dateFrom, to: form.values.dateTo },
    MAX_REGISTER_RANGE_DAYS,
  );

  return { form, range };
};
